import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, formatApiError, STATUS_LABELS } from "../../lib/api";
import { PageTitle, Card, MediaList, SingleImage } from "./AdminUI";

const FIELDS = [
  ["before_images", "Fotografie prima dell'intervento", "La prima alimenta lo slider Before/After"],
  ["after_images", "Fotografie dopo l'intervento", "La prima alimenta lo slider Before/After"],
  ["renders", "Rendering", ""],
  ["gallery", "Gallery", ""],
  ["floor_plans", "Planimetrie", "Immagini o PDF"],
];

const ProjectMedia = ({ p, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const count = FIELDS.reduce((a, [k]) => a + (p[k]?.length || 0), 0) + (p.cover_image ? 1 : 0);

  const save = async (field, value) => {
    setSaving(true);
    try {
      const payload = { ...p, [field]: value };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      await api.put(`/admin/projects/${p.id}`, payload);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Card className="!p-0 overflow-hidden" data-testid={`media-project-${p.slug}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`media-toggle-${p.slug}`} className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50">
        <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden shrink-0">{p.cover_image && <img src={imgUrl(p.cover_image)} alt="" className="img-cover" />}</div>
        <div className="flex-1 min-w-0"><div className="font-medium text-sm">{p.title} <span className="text-stone font-normal">— {p.city}</span></div><div className="text-xs text-stone">{STATUS_LABELS[p.status]} · {count} immagini {saving && "· salvataggio…"}</div></div>
        <Link to={`/admin/progetti/${p.id}`} onClick={(e) => e.stopPropagation()} className="p-2 text-stone hover:text-ink" title="Modifica scheda completa"><Pencil size={15} /></Link>
        <ChevronDown size={16} className={`text-stone transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="p-5 border-t border-gray-100 space-y-6">
          <SingleImage label="Immagine di copertina" name="cover_image" value={p.cover_image} onChange={save} folder="projects" />
          {FIELDS.map(([k, label, hint]) => <MediaList key={k} label={label} name={k} value={p[k] || []} onChange={save} folder={k === "floor_plans" ? "floorplans" : "projects"} accept={k === "floor_plans" ? "image/*,.pdf" : "image/*"} hint={hint} />)}
        </div>
      )}
    </Card>
  );
};

export default function MediaAdmin() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["admin-projects"], queryFn: () => api.get("/admin/projects").then((r) => r.data) });
  const onSaved = () => { qc.invalidateQueries({ queryKey: ["admin-projects"] }); qc.invalidateQueries({ queryKey: ["projects"] }); qc.invalidateQueries({ queryKey: ["project"] }); };
  return (
    <div data-testid="admin-media">
      <PageTitle title="Immagini dei progetti" subtitle="Carica, riordina o rimuovi foto, rendering e planimetrie: ogni modifica viene salvata subito e pubblicata sul sito" action={<Link to="/admin/progetti/nuovo" className="btn-primary !py-2.5 !px-5"><Plus size={14} /> Nuovo progetto</Link>} />
      <div className="space-y-3">{items.map((p) => <ProjectMedia key={p.id} p={p} onSaved={onSaved} />)}</div>
      {items.length === 0 && <p className="text-sm text-stone">Nessun progetto. Creane uno per iniziare a caricare immagini.</p>}
    </div>
  );
}
